import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Car, Home, MapPin, DollarSign, Search, Filter, Grid, List, X, ChevronLeft, ChevronRight, ChevronDown, ArrowLeft, MessageSquare, CreditCard, ShieldCheck } from 'lucide-react';
import { useGetUserMarketplaceListingsQuery } from '../../store/slices/apiSlice';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { usePageMetadata } from '../../hooks/usePageMetadata';
import Navbar from '../../components/common/Navbar';

const Marketplace: React.FC = () => {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState<'all' | 'car' | 'house'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [location, setLocation] = useState('');
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [manufacturer, setManufacturer] = useState('');
  const [model, setModel] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(6);

  // Set page metadata
  usePageMetadata('marketplace');

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedType, searchTerm, minPrice, maxPrice, location, featuredOnly, manufacturer, model, itemsPerPage]);

  const { data: listingsData, isLoading } = useGetUserMarketplaceListingsQuery({
    listing_type: selectedType === 'all' ? undefined : selectedType,
    page: currentPage,
    limit: itemsPerPage,
    search: searchTerm || undefined,
    min_price: minPrice ? Number(minPrice) : undefined,
    max_price: maxPrice ? Number(maxPrice) : undefined,
    location: location || undefined,
    featured: featuredOnly || undefined,
    manufacturer: manufacturer || undefined,
    model: model || undefined,
  });

  const listings = listingsData?.data?.listings || [];
  const pagination = listingsData?.data?.pagination || {
    current_page: 1,
    total_pages: 1,
    total_records: 0,
  };

  const hasActiveFilters = minPrice || maxPrice || location || featuredOnly || manufacturer || model;

  const clearFilters = () => {
    setMinPrice('');
    setMaxPrice('');
    setLocation('');
    setFeaturedOnly(false);
    setManufacturer('');
    setModel('');
    setCurrentPage(1);
  };

  const formatCurrency = (amount: number, symbol: string | null = null) => {
    const currencySymbol = symbol || '$';
    return `${currencySymbol}${amount.toLocaleString('en-US')}`;
  };

  const getPrimaryImage = (images: any[]) => {
    if (!images || images.length === 0) {
      return 'https://via.placeholder.com/400x300?text=No+Image';
    }
    const primaryImage = images.find((img) => img.is_primary);
    return primaryImage?.image_path || images[0]?.image_path || 'https://via.placeholder.com/400x300?text=No+Image';
  };

  if (isLoading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 py-8 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800"></div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          {/* Back Button */}
          <div className="mb-4">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Marketplace</h1>
              <p className="text-gray-600">Browse cars and houses available for sale</p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => navigate('/marketplace/inquiries')}
                variant="outline"
                className="flex items-center gap-2"
              >
                <MessageSquare className="w-4 h-4" />
                My Inquiries
              </Button>
              <Button
                onClick={() => navigate('/marketplace/transactions')}
                variant="outline"
                className="flex items-center gap-2"
              >
                <CreditCard className="w-4 h-4" />
                My Transactions
              </Button>
              <Button
                onClick={() => navigate('/marketplace/kyc')}
                variant="outline"
                className="flex items-center gap-2"
              >
                <ShieldCheck className="w-4 h-4" />
                KYC Verification
              </Button>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 space-y-4">
          {/* Top Row: Search, Type Filter, View Toggle */}
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by title, description, or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
              />
            </div>

            {/* Type Filter */}
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedType('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectedType === 'all'
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setSelectedType('car')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                  selectedType === 'car'
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Car className="w-4 h-4" />
                Cars
              </button>
              <button
                onClick={() => setSelectedType('house')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                  selectedType === 'house'
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Home className="w-4 h-4" />
                Houses
              </button>
            </div>

            {/* View Mode Toggle */}
            <div className="flex gap-2 border border-gray-300 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'grid' ? 'bg-gray-800 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'list' ? 'bg-gray-800 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Advanced Filters */}
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 text-gray-700 hover:text-gray-900 font-medium"
              >
                <Filter className="w-4 h-4" />
                Advanced Filters
                {hasActiveFilters && (
                  <span className="bg-gray-800 text-white text-xs px-2 py-0.5 rounded-full">
                    {[minPrice, maxPrice, location, featuredOnly, manufacturer, model].filter(Boolean).length}
                  </span>
                )}
              </button>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
                >
                  <X className="w-4 h-4" />
                  Clear Filters
                </button>
              )}
            </div>

            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                {/* Price Range */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Price Range</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      placeholder="Min"
                      value={minPrice}
                      onChange={(e) => setMinPrice(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                    />
                    <input
                      type="number"
                      placeholder="Max"
                      value={maxPrice}
                      onChange={(e) => setMaxPrice(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Location */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Location</label>
                  <input
                    type="text"
                    placeholder="Enter location..."
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  />
                </div>

                {/* Featured Only */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Options</label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={featuredOnly}
                      onChange={(e) => setFeaturedOnly(e.target.checked)}
                      className="w-4 h-4 text-gray-800 border-gray-300 rounded focus:ring-gray-500"
                    />
                    <span className="text-sm text-gray-700">Featured Only</span>
                  </label>
                </div>

                {/* Manufacturer (for cars) */}
                {selectedType === 'car' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Manufacturer</label>
                    <input
                      type="text"
                      placeholder="e.g., Toyota, Honda..."
                      value={manufacturer}
                      onChange={(e) => setManufacturer(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                    />
                  </div>
                )}

                {/* Model (for cars) */}
                {selectedType === 'car' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Model</label>
                    <input
                      type="text"
                      placeholder="e.g., Camry, Accord..."
                      value={model}
                      onChange={(e) => setModel(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Results Count and Per Page Selection */}
        <div className="mb-4 flex items-center justify-between flex-wrap gap-4">
          <p className="text-sm text-gray-600">
            Showing {listings.length} of {pagination.total_records} {pagination.total_records === 1 ? 'listing' : 'listings'}
            {pagination.total_pages > 1 && ` (Page ${pagination.current_page} of ${pagination.total_pages})`}
          </p>
          
          {/* Per Page Selection */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-700 font-medium">Items per page:</label>
            <div className="relative">
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 text-sm font-medium text-gray-900 focus:ring-2 focus:ring-gray-500 focus:border-transparent cursor-pointer"
              >
                <option value={6}>6</option>
                <option value={12}>12</option>
                <option value={24}>24</option>
                <option value={36}>36</option>
                <option value={48}>48</option>
              </select>
              <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Listings Grid/List */}
        {listings.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="text-gray-400 mb-4">
              {selectedType === 'car' ? (
                <Car className="w-16 h-16 mx-auto" />
              ) : selectedType === 'house' ? (
                <Home className="w-16 h-16 mx-auto" />
              ) : (
                <Filter className="w-16 h-16 mx-auto" />
              )}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Listings Found</h3>
            <p className="text-gray-600">
              {searchTerm
                ? 'Try adjusting your search criteria'
                : 'No listings available at the moment'}
            </p>
          </Card>
        ) : (
          <div
            className={
              viewMode === 'grid'
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'
                : 'space-y-4'
            }
          >
            {listings.map((listing) => (
              <Link
                key={listing.listing_id}
                to={`/marketplace/${listing.listing_id}`}
                className="block"
              >
                <Card className="overflow-hidden hover:shadow-lg transition-shadow">
                  {/* Image */}
                  <div className="relative h-48 bg-gray-200">
                    <img
                      src={getPrimaryImage(listing.images)}
                      alt={listing.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x300?text=No+Image';
                      }}
                    />
                    <div className="absolute top-3 left-3">
                      <Badge
                        variant={listing.listing_type === 'car' ? 'secondary' : 'default'}
                        className="bg-white text-gray-900"
                      >
                        {listing.listing_type === 'car' ? (
                          <Car className="w-3 h-3 inline mr-1" />
                        ) : (
                          <Home className="w-3 h-3 inline mr-1" />
                        )}
                        {listing.listing_type === 'car' ? 'Car' : 'House'}
                      </Badge>
                    </div>
                    {listing.is_featured && (
                      <div className="absolute top-3 right-3">
                        <Badge variant="default" className="bg-yellow-500 text-white">
                          Featured
                        </Badge>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                      {listing.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {listing.description}
                    </p>

                    {/* Details */}
                    <div className="space-y-2 mb-4">
                      {listing.location && (
                        <div className="flex items-center text-sm text-gray-600">
                          <MapPin className="w-4 h-4 mr-2" />
                          <span className="truncate">{listing.location}</span>
                        </div>
                      )}
                      <div className="flex items-center text-lg font-bold text-gray-900">
                        <DollarSign className="w-5 h-5 mr-1" />
                        {formatCurrency(listing.price, listing.currency_symbol)}
                      </div>
                    </div>

                    {/* View Details Button */}
                    <button className="w-full bg-gray-800 text-white py-2 px-4 rounded-lg hover:bg-gray-900 transition-colors text-sm font-medium">
                      View Details
                    </button>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-2">
            <Button
              variant="outline"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-1"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>

            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                let pageNum: number;
                if (pagination.total_pages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= pagination.total_pages - 2) {
                  pageNum = pagination.total_pages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                      currentPage === pageNum
                        ? 'bg-gray-800 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <Button
              variant="outline"
              onClick={() => setCurrentPage((prev) => Math.min(pagination.total_pages, prev + 1))}
              disabled={currentPage === pagination.total_pages}
              className="flex items-center gap-1"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
      </div>
    </>
  );
};

export default Marketplace;
